import React, { FunctionComponent, useState, useContext, useEffect, JSX } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";
import ListHeadComponent, { ISortableProps } from "./list-head";
import { Order } from "./model";
import { EllipsisTooltip } from "../ellipsis-tooltip/ellipsis-tooltip";
import { AppConfig } from "../../context/context";
import { APP_LANGUAGES } from "../../utils/constants";
import { useTranslation } from "react-i18next";
import BlueCheckbox from "../checkbox/checkbox";

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: "750px",
  color: "var(--kyocera-dark-grey)",
  "& tbody": {
   /* "& tr.MuiTableRow-root.MuiTableRow-hover:hover": {
      backgroundColor: "#e1f7ff",
      cursor: "pointer",
    },*/
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  //display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  //minWidth: "100px",
  //maxWidth: "400px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StatusHolder = styled("div")(({ theme }) => ({
  marginLeft: "20px",
  border: "1px solid #000",
  borderRadius: "5px",
  padding: "5px",
}));

const CutCellText = styled("div")(({ theme }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0
}));

export type IListConfig = {
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (value: number) => void;
  rowsPerPageOptions: number[];
  total: number;
};

type IListProps = {
  rows: any[];
  headCells: any[];
  listConfig: IListConfig;
  onSelectionChange?: (selectedIds: string[]) => void;
  selectedRowsProp?: string[]
};

const ListComponent: FunctionComponent<IListProps & ISortableProps> = (props) => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("id");
  const { config } = useContext(AppConfig);
  const { t } = useTranslation();

  const { rows, headCells, listConfig, onSelectionChange, selectedRowsProp, sortable } = props;
  const {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    rowsPerPageOptions,
    total,
  } = listConfig;

  const [selectedRows, setSelectedRows] = useState<string[]>(selectedRowsProp || []); // Initialize from prop
  const [allChecked, setAllChecked] = useState(false); // State for "check all"

  useEffect(() => {
    if(rows.length > 0) {
      setAllChecked(selectedRows.length === rows.length);
    } else {
      setAllChecked(false);
    }
  }, [selectedRows, rows.length]);

  useEffect(() => {
    setSelectedRows(selectedRowsProp || []); // Update selection when props change
  }, [selectedRowsProp]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
    //TBD
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, id: string) => { // Use row.id
    const isChecked = event.target.checked;
    let updatedSelectedRows = [...selectedRows];  // Correctly declare and initialize updatedSelectedRows

    if (isChecked) {
      updatedSelectedRows.push(id); // push the id of the checked row
    } else {
      updatedSelectedRows = updatedSelectedRows.filter((rowId) => rowId !== id); // remove the id of unchecked row
    }

    setSelectedRows(updatedSelectedRows) // Update local selectedRows state

    if (onSelectionChange) {
      onSelectionChange(updatedSelectedRows); // Now you can call the callback
    }
  };

  const handleCheckAllChange = (checked: boolean) => {
    setAllChecked(checked);    // Update state

    let updatedRows = [];
    if(checked){ // Check all
        updatedRows = rows.map((row) => row.id)
    }
    setSelectedRows(updatedRows);
    // Pass updated selections to callback which handles updating selectedRows
    props.onSelectionChange && props.onSelectionChange(updatedRows);  
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    // Reset selection when changing page
    setSelectedRows([]);
    setAllChecked(false);
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
    // Reset selection when changing rows per page
    setSelectedRows([]);
    setAllChecked(false);
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  };

  const textHandler = (text: string) => {
    if (text === "Provider") return t("cmp_organization_provider_label");
    if (text === "Customer") return t("cmp_organization_customer_label");
    return text;
  };

  const renderTableRow = (row: any) => {
    let cells: JSX.Element[] = [];
    const stopPropagation = (event: React.MouseEvent<unknown>) => event.stopPropagation();

    Object.keys(row).forEach((k, index) => {
      const headCell: any = headCells.find((cell) => cell.id === k);
      
      const renderStatus = () => {
        if (!row.id) return null;
        if (row.email === null || row.email === "") {
          return <div>{t("cmp_organization_expired")}</div>;
        }
        return <div>{row.status === false ? t("cmp_organization_status_inactive") : t("cmp_organization_status_active")}</div>;
      };

      const renderCheckbox = (row: any) => (
        <div onClick={e => e.stopPropagation()}>
          <BlueCheckbox
            color="primary"
            checked={selectedRows.includes(row.id)} 
            onChange={(e) => handleCheckboxChange(e, row.id)}
            name={row.id}
          />
        </div>
      );

      if (k !== "id" && headCell && headCell.display) {
        cells.push(
          <StyledTableCell key={`${k}-${index}`}>
            <EllipsisTooltip title={typeof row[k] === "string" ? textHandler(row[k]) : ""}>
              <div style={{  display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      minWidth: "50px",
                      maxWidth: "300px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis", }}>
                <CutCellText>
                  {headCell.id === "status" && renderStatus()}
                  {headCell.id === "checkbox" && renderCheckbox(row)}
                  {headCell.id !== "checkbox" && headCell.id !== "status" && (
                    headCell.id === "url" ? (
                      <a
                        href={textHandler(row[k])}
                        target="_blank"
                        rel="noreferrer"
                        onClick={stopPropagation}
                      >
                        {textHandler(row[k])}
                      </a>
                    ) : textHandler(row[k])
                  )}
                </CutCellText>
              </div>
            </EllipsisTooltip>
          </StyledTableCell>
        );
      }
    });

    return cells;
  };

  return (
    <ThemeProvider theme={createTheme({}, APP_LANGUAGES[config.language]?.muiMapping)}>
      <TableContainer>
        <StyledTable aria-labelledby="list" aria-label="list">
          <ListHeadComponent
            sortable={sortable}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            headCells={headCells}
            allChecked={allChecked}
            onCheckAllChange={handleCheckAllChange}
          />
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                hover
                onClick={(event) => handleClick(event, String(row.id))}
                tabIndex={-1}
                key={`${row.id}-${index}`}
              >
                {renderTableRow(row)}
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </ThemeProvider>
  );
};

export default ListComponent;
